import { Village } from '@/interfaces';
import { Building, Globe, Home, Info, Landmark, Map, MapPin, PinIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface VoterLocationProps {
  village: Village;
}

export const VoterLocation = ({ village }: VoterLocationProps) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-4">
      <div className="space-y-5 rounded-md border border-[#e2e9f3] bg-[#fbfcfe] p-5 sm:p-6">
        <h5 className="flex items-center gap-3 border-b border-[#e8eef6] pb-4 font-semibold text-[#172641]">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-[#eaf3ff] text-[#2374cf]">
            <MapPin className="h-4 w-4" />
          </span>
          {t('TextVotingLocation')}
        </h5>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {village.cell.sector.district.province.name && (
            <div className="flex items-start gap-3 rounded-md border border-[#e5ebf3] bg-white p-4">
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-[#f0f5fb] text-[#60708a]">
                <Globe className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium text-[#8290a5]">{t('TextProvince')}</p>
                <p className="text-sm mt-0.5 truncate font-medium text-[#263650]">
                  {village.cell.sector.district.province.name}
                </p>
              </div>
            </div>
          )}

          {village.cell.sector.district.name && (
            <div className="flex items-start gap-3 rounded-md border border-[#e5ebf3] bg-white p-4">
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-[#f0f5fb] text-[#60708a]">
                <Map className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium text-[#8290a5]">{t('TextDistrict')}</p>
                <p className="text-sm mt-0.5 truncate font-medium text-[#263650]">
                  {village.cell.sector.district.name}
                </p>
              </div>
            </div>
          )}

          {village.cell.sector.name && (
            <div className="flex items-start gap-3 rounded-md border border-[#e5ebf3] bg-white p-4">
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-[#f0f5fb] text-[#60708a]">
                <Landmark className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium text-[#8290a5]">{t('TextSector')}</p>
                <p className="text-sm mt-0.5 truncate font-medium text-[#263650]">
                  {village.cell.sector.name}
                </p>
              </div>
            </div>
          )}

          {village.cell.name && (
            <div className="flex items-start gap-3 rounded-md border border-[#e5ebf3] bg-white p-4">
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-[#f0f5fb] text-[#60708a]">
                <Building className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium text-[#8290a5]">{t('TextCell')}</p>
                <p className="text-sm mt-0.5 truncate font-medium text-[#263650]">
                  {village.cell.name}
                </p>
              </div>
            </div>
          )}

          {village.name && (
            <div className="flex items-start gap-3 rounded-md border border-[#e5ebf3] bg-white p-4">
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-[#f0f5fb] text-[#60708a]">
                <Home className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium text-[#8290a5]">{t('TextVillage')}</p>
                <p className="text-sm mt-0.5 truncate font-medium text-[#263650]">{village.name}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {village.votingStation && (
        <div className="space-y-5 rounded-md border border-[#dcece3] bg-[#f7fcf9] p-5 sm:p-6">
          <h5 className="flex items-center gap-3 border-b border-[#e0eee6] pb-4 font-semibold text-[#172641]">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-[#e4f5ea] text-[#208b52]">
              <PinIcon className="h-4 w-4" />
            </span>
            {t('TextPollingDetails')}
          </h5>

          <div className="space-y-4">
            <div className="flex items-start gap-3 rounded-md border border-[#dfece4] bg-white p-4">
              <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-[#eef8f2] text-[#208b52]">
                <MapPin className="h-4 w-4" />
              </div>
              <div>
                <p className="text-xs font-medium text-[#8290a5]">{t('TextPollingStation')}</p>
                <p className="text-sm mt-0.5 font-semibold text-[#263650]">
                  {village.votingStation.code} {village.votingStation.name}
                </p>
              </div>
            </div>

            <div className="flex gap-3 rounded-md border border-[#dce8f7] bg-[#f3f8ff] p-4 text-sm">
              <Info className="text-sm mt-0.5 h-4 w-4 shrink-0 text-[#2374cf]" />
              <p className="leading-6 text-[#52617a]">
                <span className="font-semibold text-[#2374cf]">{t('TextNote')}:</span>{' '}
                {t('TextPollingStationNote')}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
